import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import {
    cellHue,
    computeVoronoiCells,
    generateGridSites,
    generateRandomSites,
    seededRNG,
    type VoronoiCellPath,
} from "../../utils/voronoiSites";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export type VoronoiSiteMode = "random" | "grid";
export type VoronoiFillMode = "none" | "solid" | "perCell";

interface VoronoiState {
    canvasW: number;
    canvasH: number;
    padding: number;
    siteCount: number;
    seed: string;
    siteMode: VoronoiSiteMode;
    gridJitter: number;
    strokeColor: string;
    strokeWidth: number;
    fillColor: string;
    fillMode: VoronoiFillMode;
    showSites: boolean;
    setSiteCount: (v: number) => void;
    setSeed: (v: string) => void;
    setSiteMode: (v: VoronoiSiteMode) => void;
    setGridJitter: (v: number) => void;
    setStrokeColor: (v: string) => void;
    setStrokeWidth: (v: number) => void;
    setFillColor: (v: string) => void;
    setFillMode: (v: VoronoiFillMode) => void;
    setShowSites: (v: boolean) => void;
    viewBoxAttr: string;
    sites: [number, number][];
    cells: VoronoiCellPath[];
    fillForCell: (index: number) => string;
    canExport: boolean;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    randomizeParameters: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const VoronoiContext = createContext<VoronoiState | null>(null);

const DEFAULT_VORONOI_PRESET: Record<string, unknown> = {
    siteCount: 48,
    seed: "voronoi-1",
    siteMode: "random",
    gridJitter: 0.35,
    strokeColor: "#222222",
    strokeWidth: 1,
    fillColor: "#88aacc",
    fillMode: "perCell",
    showSites: true,
};

export function useVoronoi() {
    const ctx = useContext(VoronoiContext);
    if (!ctx)
        throw new Error("useVoronoi must be used within VoronoiProvider");
    return ctx;
}

export function VoronoiProvider({ children }: { children: React.ReactNode }) {
    const [canvasW] = useState(500);
    const [canvasH] = useState(500);
    const [padding] = useState(24);
    const [siteCount, setSiteCountState] = useState(48);
    const [seed, setSeed] = useState("voronoi-1");
    const [siteMode, setSiteMode] = useState<VoronoiSiteMode>("random");
    const [gridJitter, setGridJitter] = useState(0.35);
    const [strokeColor, setStrokeColor] = useState("#222222");
    const [strokeWidth, setStrokeWidth] = useState(1);
    const [fillColor, setFillColor] = useState("#88aacc");
    const [fillMode, setFillMode] = useState<VoronoiFillMode>("perCell");
    const [showSites, setShowSites] = useState(true);

    const setSiteCount = useCallback((v: number) => {
        setSiteCountState(Math.max(1, Math.min(500, Math.round(v))));
    }, []);

    const viewBoxAttr = useMemo(
        () =>
            `${-padding} ${-padding} ${canvasW + padding * 2} ${canvasH + padding * 2}`,
        [canvasW, canvasH, padding],
    );

    const sites = useMemo(() => {
        const n = Math.max(1, siteCount);
        const rng = seededRNG(seed);
        if (siteMode === "grid") {
            return generateGridSites(n, canvasW, canvasH, gridJitter, rng);
        }
        return generateRandomSites(n, canvasW, canvasH, rng);
    }, [siteCount, seed, siteMode, gridJitter, canvasW, canvasH]);

    const bounds = useMemo(
        (): [number, number, number, number] => [0, 0, canvasW, canvasH],
        [canvasW, canvasH],
    );

    const cells = useMemo(
        () => computeVoronoiCells(sites, bounds),
        [sites, bounds],
    );

    const canExport = cells.length > 0;

    const fillForCell = useCallback(
        (index: number): string => {
            if (fillMode === "none") return "none";
            if (fillMode === "solid") return fillColor;
            const h = cellHue(seed, index);
            return `hsl(${h} 48% 62%)`;
        },
        [fillMode, fillColor, seed],
    );

    const buildExportSvgString = useCallback((): string => {
        const bg = `<rect x="${-padding}" y="${-padding}" width="${canvasW + padding * 2}" height="${canvasH + padding * 2}" fill="white" />`;
        const paths = cells
            .map(({ pathD, index }) => {
                const fill = fillForCell(index);
                const fillAttr =
                    fill === "none"
                        ? 'fill="none"'
                        : `fill="${fill}"`;
                return `<path d="${pathD}" ${fillAttr} stroke="${strokeColor}" stroke-width="${strokeWidth}" />`;
            })
            .join("\n    ");
        const siteDots = showSites
            ? sites
                  .map(
                      ([x, y]) =>
                          `<circle cx="${x}" cy="${y}" r="2.5" fill="${strokeColor}" />`,
                  )
                  .join("\n    ")
            : "";
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  ${bg}
  <g class="voronoi-cells">
    ${paths}
  </g>
  <g class="voronoi-sites">
    ${siteDots}
  </g>
</svg>`;
    }, [
        cells,
        sites,
        showSites,
        padding,
        canvasW,
        canvasH,
        strokeColor,
        strokeWidth,
        fillForCell,
        viewBoxAttr,
    ]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `voronoi-${seed}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [canExport, buildExportSvgString, seed]);

    const randomizeParameters = useCallback(() => {
        const rng = seededRNG(Date.now().toString());
        const randI = (min: number, max: number) =>
            Math.floor(rng() * (max - min + 1)) + min;
        const randF = (min: number, max: number) => rng() * (max - min) + min;
        const byte = (lo: number, hi: number) =>
            Math.floor(rng() * (hi - lo + 1) + lo)
                .toString(16)
                .padStart(2, "0");

        setSeed(`v-${Math.floor(rng() * 90000 + 10000)}`);
        setSiteCount(randI(12, 120));
        setSiteMode(rng() < 0.5 ? "random" : "grid");
        setGridJitter(parseFloat(randF(0, 0.85).toFixed(2)));
        setStrokeWidth(parseFloat(randF(0.5, 2.5).toFixed(1)));
        setFillMode(
            rng() < 0.33 ? "none" : rng() < 0.66 ? "solid" : "perCell",
        );
        setShowSites(rng() < 0.7);
        setStrokeColor(`#${byte(15, 80)}${byte(15, 80)}${byte(15, 80)}`);
        setFillColor(`#${byte(80, 200)}${byte(90, 210)}${byte(100, 220)}`);
    }, [setSiteCount]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            siteCount, seed, siteMode, gridJitter, strokeColor,
            strokeWidth, fillColor, fillMode, showSites,
        }),
        [
            siteCount, seed, siteMode, gridJitter, strokeColor,
            strokeWidth, fillColor, fillMode, showSites,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.siteCount != null) setSiteCount(values.siteCount as number);
        if (values.seed != null) setSeed(values.seed as string);
        if (values.siteMode != null) setSiteMode(values.siteMode as VoronoiSiteMode);
        if (values.gridJitter != null) setGridJitter(values.gridJitter as number);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
        if (values.strokeWidth != null) setStrokeWidth(values.strokeWidth as number);
        if (values.fillColor != null) setFillColor(values.fillColor as string);
        if (values.fillMode != null) setFillMode(values.fillMode as VoronoiFillMode);
        if (values.showSites != null) setShowSites(values.showSites as boolean);
    }, [setSiteCount]);

    const { resetParameters } = useToolParamPersistence({
        toolId: "voronoi-diagram",
        defaultPreset: DEFAULT_VORONOI_PRESET,
        getPresetValues,
        applyPreset,
    });

    const value: VoronoiState = {
        canvasW,
        canvasH,
        padding,
        siteCount,
        seed,
        siteMode,
        gridJitter,
        strokeColor,
        strokeWidth,
        fillColor,
        fillMode,
        showSites,
        setSiteCount,
        setSeed,
        setSiteMode,
        setGridJitter,
        setStrokeColor,
        setStrokeWidth,
        setFillColor,
        setFillMode,
        setShowSites,
        viewBoxAttr,
        sites,
        cells,
        fillForCell,
        canExport,
        buildExportSvgString,
        handleDownload,
        randomizeParameters,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <VoronoiContext.Provider value={value}>
            {children}
        </VoronoiContext.Provider>
    );
}
